import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LayoutDashboard, LogOut, Menu, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import onHomeLogo from "@/images/logo/onhome.svg";
import TransitionLink from "./TransitionLink";
import CtaLink from "./site/CtaLink";
import { GET_STARTED_PATH, siteNavLinks } from "./site/nav";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const menuContentClass = "min-w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-lg";
const itemClass =
  "cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:text-navy";

const Header = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout: authLogout } = useAuth();

  const logout = async () => {
    await authLogout();
    navigate("/");
    window.location.reload();
  };

  const isSignedIn = isAuthenticated && !!user;
  const displayName = user?.firstName || (user?.email ? user.email.substring(0, user.email.indexOf("@")) : "Account");
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const accountItems = isSignedIn ? (
    <>
      <DropdownMenuItem onSelect={() => navigate("/dashboard")} className={itemClass}>
        <LayoutDashboard className="size-4 opacity-60" />
        Dashboard
      </DropdownMenuItem>
      {user?.roles?.includes("ROLE_ADMIN") && (
        <DropdownMenuItem onSelect={() => navigate("/admin")} className={itemClass}>
          <Shield className="size-4 opacity-60" />
          Admin Portal
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator className="my-1 h-px bg-slate-200" />
      <DropdownMenuItem
        onSelect={logout}
        className="cursor-pointer rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-600"
      >
        <LogOut className="size-4 opacity-60" />
        Logout
      </DropdownMenuItem>
    </>
  ) : (
    <DropdownMenuItem onSelect={() => navigate("/login")} className={itemClass}>
      Log in
    </DropdownMenuItem>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-[1200px] items-center justify-between gap-6 px-5 sm:px-8">
        <TransitionLink to="/" className="shrink-0">
          <img src={onHomeLogo} alt="OnHome" className="h-8 w-auto sm:h-9" />
        </TransitionLink>

        <nav aria-label="Main" className="hidden items-center gap-10 lg:flex">
          {siteNavLinks.map((link) => (
            <TransitionLink
              key={link.to}
              to={link.to}
              aria-current={isActive(link.to) ? "page" : undefined}
              className={cn(
                "relative py-2 text-[15px] text-slate-600 transition-colors hover:text-navy",
                isActive(link.to) &&
                  "font-medium text-navy after:absolute after:-inset-x-3 after:-bottom-2 after:h-0.5 after:rounded-full after:bg-brand",
              )}
            >
              {link.label}
            </TransitionLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {isSignedIn ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="hidden h-10 cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 px-4 text-sm font-medium text-navy outline-none hover:bg-slate-50 lg:inline-flex">
                Hi {displayName}
                <ChevronDown className="size-4 opacity-60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className={menuContentClass}>
                {accountItems}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <TransitionLink
              to="/login"
              className="hidden text-[15px] text-slate-600 transition-colors hover:text-navy lg:inline"
            >
              Log in
            </TransitionLink>
          )}

          <CtaLink to={GET_STARTED_PATH} size="sm" arrow={false} className="hidden sm:inline-flex">
            Help me BuySooner
          </CtaLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Open menu"
              className="inline-flex size-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 text-navy outline-none hover:bg-slate-50 lg:hidden"
            >
              <Menu className="size-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className={menuContentClass}>
              {siteNavLinks.map((link) => (
                <DropdownMenuItem key={link.to} onSelect={() => navigate(link.to)} className={itemClass}>
                  {link.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onSelect={() => navigate(GET_STARTED_PATH)}
                className={cn(itemClass, "font-semibold text-navy sm:hidden")}
              >
                Help me BuySooner
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1 h-px bg-slate-200" />
              {accountItems}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
